import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Trash2,
  Calendar,
  Building2,
  Store,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Upload,
  Loader2,
  File,
  FilePlus,
} from 'lucide-react';

const DOCUMENT_TYPES = [
  { id: 'lease_agreement', label: 'Lease Agreement', icon: FileText },
  { id: 'addendum', label: 'Addendum', icon: FilePlus },
  { id: 'renewal', label: 'Renewal Agreement', icon: File },
  { id: 'termination', label: 'Termination Notice', icon: AlertTriangle },
  { id: 'rent_receipt', label: 'Rent Receipt', icon: File },
];

const STATUS_COLORS = {
  draft: 'bg-slate-500',
  pending_approval: 'bg-amber-500',
  approved: 'bg-emerald-500',
  expired: 'bg-red-500',
};

export default function LeaseDocumentsPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [malls, setMalls] = useState([]);
  const [shops, setShops] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterMall, setFilterMall] = useState('all');
  const [newDocOpen, setNewDocOpen] = useState(false);
  const [viewDocOpen, setViewDocOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [docForm, setDocForm] = useState({
    shop_id: '',
    document_type: '',
    title: '',
    description: '',
    file_url: '',
    effective_date: '',
    expiry_date: '',
  });

  useEffect(() => {
    fetchData();
  }, [filterMall]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const query = filterMall !== 'all' ? `?mall_id=${filterMall}` : '';
      const [docsRes, mallsRes] = await Promise.all([
        api.get(`/documents/${query}`),
        api.get('/malls/'),
      ]);
      setDocuments(docsRes.data);
      setMalls(mallsRes.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchShopsForMall = async (mallId) => {
    try {
      const res = await api.get(`/shops/?mall_id=${mallId}`);
      setShops(res.data);
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  const handleMallSelectForForm = (mallId) => {
    fetchShopsForMall(mallId);
    setDocForm(f => ({ ...f, shop_id: '' }));
  };

  const handleCreateDocument = async () => {
    if (!docForm.shop_id || !docForm.document_type || !docForm.title) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/documents/', docForm);
      toast.success('Document created successfully');
      setNewDocOpen(false);
      setDocForm({
        shop_id: '',
        document_type: '',
        title: '',
        description: '',
        file_url: '',
        effective_date: '',
        expiry_date: '',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (docId, newStatus) => {
    try {
      await api.put(`/documents/${docId}`, { status: newStatus });
      toast.success('Document status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredDocs = documents.filter(doc => {
    const matchesSearch = !searchQuery || 
      doc.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.brand_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.unit_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || doc.document_type === filterType;
    return matchesSearch && matchesType;
  });

  const expiringDocs = documents.filter(doc => {
    if (!doc.expiry_date) return false;
    const expiry = new Date(doc.expiry_date);
    const now = new Date();
    const daysUntilExpiry = (expiry - now) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry > 0 && daysUntilExpiry <= 90;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="documents-loading">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
          <p className="text-muted-foreground">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lease-documents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Lease Documents</h1>
          <p className="text-muted-foreground">
            Manage lease agreements, addendums, and other documents
          </p>
        </div>
        <Dialog open={newDocOpen} onOpenChange={setNewDocOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-document-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Document</DialogTitle>
              <DialogDescription>
                Upload or create a new lease document
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Mall *</Label>
                <Select onValueChange={handleMallSelectForForm}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select mall" />
                  </SelectTrigger>
                  <SelectContent>
                    {malls.map(mall => (
                      <SelectItem key={mall.id} value={mall.id}>{mall.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Shop/Unit *</Label>
                <Select 
                  value={docForm.shop_id} 
                  onValueChange={(v) => setDocForm(f => ({ ...f, shop_id: v }))}
                  disabled={shops.length === 0}
                >
                  <SelectTrigger data-testid="shop-select">
                    <SelectValue placeholder={shops.length === 0 ? "Select mall first" : "Select shop"} />
                  </SelectTrigger>
                  <SelectContent>
                    {shops.map(shop => (
                      <SelectItem key={shop.id} value={shop.id}>
                        {shop.unit_number} - {shop.brand_name || 'Vacant'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Document Type *</Label>
                <Select 
                  value={docForm.document_type} 
                  onValueChange={(v) => setDocForm(f => ({ ...f, document_type: v }))}
                >
                  <SelectTrigger data-testid="doc-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(type => (
                      <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  placeholder="e.g., Lease Agreement - Zara 2024"
                  value={docForm.title}
                  onChange={(e) => setDocForm(f => ({ ...f, title: e.target.value }))}
                  data-testid="doc-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Additional notes..."
                  rows={2}
                  value={docForm.description}
                  onChange={(e) => setDocForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Effective Date</Label>
                  <Input
                    type="date"
                    value={docForm.effective_date}
                    onChange={(e) => setDocForm(f => ({ ...f, effective_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expiry Date</Label>
                  <Input
                    type="date"
                    value={docForm.expiry_date}
                    onChange={(e) => setDocForm(f => ({ ...f, expiry_date: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>File URL (optional)</Label>
                <Input
                  placeholder="https://drive.google.com/..."
                  value={docForm.file_url}
                  onChange={(e) => setDocForm(f => ({ ...f, file_url: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">Link to Google Drive, Dropbox, or other cloud storage</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewDocOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateDocument} disabled={submitting} data-testid="submit-doc-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Document
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Expiring Documents Alert */}
      {expiringDocs.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-500">Documents Expiring Soon</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  {expiringDocs.length} document(s) expiring within 90 days
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {expiringDocs.slice(0, 3).map(doc => (
                    <Badge key={doc.id} variant="outline" className="text-xs">
                      {doc.brand_name || doc.unit_number} - {formatDate(doc.expiry_date)}
                    </Badge>
                  ))}
                  {expiringDocs.length > 3 && (
                    <Badge variant="outline" className="text-xs">+{expiringDocs.length - 3} more</Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{documents.length}</p>
                <p className="text-xs text-muted-foreground">Total Documents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{documents.filter(d => d.status === 'approved').length}</p>
                <p className="text-xs text-muted-foreground">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{documents.filter(d => d.status === 'pending_approval').length}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{expiringDocs.length}</p>
                <p className="text-xs text-muted-foreground">Expiring Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by title, brand, or unit..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-docs-input"
          />
        </div>
        <Select value={filterMall} onValueChange={setFilterMall}>
          <SelectTrigger className="w-[200px]">
            <Building2 className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by mall" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Malls</SelectItem>
            {malls.map(mall => (
              <SelectItem key={mall.id} value={mall.id}>{mall.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {DOCUMENT_TYPES.map(type => (
              <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Documents List */}
      {filteredDocs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Documents Found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || filterType !== 'all' ? 'Try adjusting your filters' : 'Start by adding your first document'}
            </p>
            <Button onClick={() => setNewDocOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredDocs.map(doc => (
            <Card key={doc.id} className="hover:border-primary/30 transition-colors" data-testid={`doc-card-${doc.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${STATUS_COLORS[doc.status]}/10`}>
                      <FileText className={`w-6 h-6 ${STATUS_COLORS[doc.status].replace('bg-', 'text-')}`} />
                    </div>
                    <div>
                      <h4 className="font-medium">{doc.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {DOCUMENT_TYPES.find(t => t.id === doc.document_type)?.label || doc.document_type}
                        </Badge>
                        <Badge className={`text-xs ${STATUS_COLORS[doc.status]}`}>
                          {doc.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Store className="w-3 h-3" />
                          {doc.unit_number}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {doc.brand_name || 'Vacant'}
                        </span>
                        {doc.expiry_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Expires: {formatDate(doc.expiry_date)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedDoc(doc);
                        setViewDocOpen(true);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {doc.file_url && (
                      <Button variant="ghost" size="sm" asChild>
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                          <Download className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => handleDeleteDocument(doc.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Document Dialog */}
      <Dialog open={viewDocOpen} onOpenChange={setViewDocOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedDoc?.title}</DialogTitle>
            <DialogDescription>Document details and actions</DialogDescription>
          </DialogHeader>
          
          {selectedDoc && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                <Badge className={`${STATUS_COLORS[selectedDoc.status]}`}>
                  {selectedDoc.status?.replace('_', ' ')}
                </Badge>
                <Badge variant="outline">
                  {DOCUMENT_TYPES.find(t => t.id === selectedDoc.document_type)?.label}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Mall</p>
                  <p className="font-medium">{selectedDoc.mall_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Unit</p>
                  <p className="font-medium">{selectedDoc.unit_number}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Brand</p>
                  <p className="font-medium">{selectedDoc.brand_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-medium">{formatDate(selectedDoc.created_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Effective Date</p>
                  <p className="font-medium">{formatDate(selectedDoc.effective_date)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Expiry Date</p>
                  <p className="font-medium">{formatDate(selectedDoc.expiry_date)}</p>
                </div>
              </div>

              {selectedDoc.description && (
                <div>
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p className="text-sm bg-muted/50 p-3 rounded-lg mt-1">{selectedDoc.description}</p>
                </div>
              )}

              <div className="pt-4 border-t">
                <p className="text-sm font-medium mb-2">Update Status</p>
                <div className="flex gap-2">
                  {['draft', 'pending_approval', 'approved', 'expired'].map(status => (
                    <Button
                      key={status}
                      variant={selectedDoc.status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        handleUpdateStatus(selectedDoc.id, status);
                        setSelectedDoc({ ...selectedDoc, status });
                      }}
                    >
                      {status.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDocOpen(false)}>Close</Button>
            {selectedDoc?.file_url && (
              <Button asChild>
                <a href={selectedDoc.file_url} target="_blank" rel="noopener noreferrer">
                  <Download className="w-4 h-4 mr-2" />
                  Open Document
                </a>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
